import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, LayoutGrid, HardHat } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { MarketingNav, MarketingFooter, MarketingCTA } from "@/components/MarketingChrome";
import { SOLUTIONS } from "@/data/seoContent";

export default function SolutionsIndex() {
  const jsonLd = breadcrumbLd([{ name: "Home", path: "/" }, { name: "Solutions", path: "/solutions" }]);
  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen">
      <Seo
        title="Solutions, HOA Communication, AI Answering & Community Software | Ask Redline"
        description="HOA communication software, an AI answering service for property management, self-managed HOA tools, condo association software and more, one AI concierge, by text and phone."
        canonical="/solutions"
        jsonLd={jsonLd}
        scrollTop
      />
      <MarketingNav />

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
            <LayoutGrid className="h-3.5 w-3.5" strokeWidth={2} /> Solutions
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">One concierge. Every kind of community.</h1>
          <p className="mt-5 text-lg text-ink-700 leading-relaxed">
            Whether you're a volunteer board, a high-rise condo or a management company running thousands of doors, Ask Redline
            answers residents by text and phone, grounded in your own documents. Find the fit for you.
          </p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-2 gap-5">
        {SOLUTIONS.map((s, i) => (
          <motion.div key={s.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: (i % 2) * 0.06 }}>
            <Link to={`/solutions/${s.slug}`} data-testid={`solution-card-${s.slug}`}
              className="group flex flex-col h-full rounded-3xl border border-sand-200 bg-white p-8 hover:border-redline-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-24px_rgba(15,15,20,0.25)] transition-all">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-redline-500">{s.hero.eyebrow}</div>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight mt-2 group-hover:text-redline-600 transition-colors">{s.hero.title}</h2>
              <p className="text-sm text-ink-600 mt-3 leading-relaxed flex-1">{s.hero.sub}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-redline-600 group-hover:gap-2.5 transition-all">
                Explore <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </motion.div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <Link to="/field-services" data-testid="solutions-field-services-card"
          className="group block rounded-3xl border border-sand-200 bg-white p-8 hover:border-redline-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-24px_rgba(15,15,20,0.25)] transition-all">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-redline-50 flex items-center justify-center shrink-0">
              <HardHat className="h-5 w-5 text-redline-600" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-redline-500">Add-on module</div>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight mt-1 group-hover:text-redline-600 transition-colors">
                Vendors, work orders and invoices
              </h2>
              <p className="text-sm text-ink-600 mt-3 leading-relaxed max-w-2xl">
                Answering the resident is half the job. Field Services runs the other half, insurance that gates
                live work, crews texted directly, photo proof that closes the ticket, and invoices that arrive
                already coded.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-redline-600 group-hover:gap-2.5 transition-all">
                Explore Field Services <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      <MarketingCTA />
      <MarketingFooter />
    </div>
  );
}
